// src/pages/SummaryReports/SummaryReportsPage.jsx

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';
import SummaryReportsTable from '../../components/Directory/SummaryReportsTable';
import { FaSearch, FaFilter, FaUpload } from 'react-icons/fa';
import './SummaryReportsPage.css';
import './SummaryAnalysis.css';
import RiskPriorityTable from '../../components/Directory/RiskPriorityTable';
import API_BASE_URL from '../../config';


// Регистрируем нужные компоненты Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const SummaryReportsPage = () => {
  // ========== Общий state для переключения вкладок ==========
  const [activeTab, setActiveTab] = useState('analysis'); // 'analysis' или 'reports'

  // ========== State для вкладки «Сводный анализ» ==========
  const [loading, setLoading] = useState(true);
  const [prodPct, setProdPct] = useState(0); // процент продуктивности (0–100)
  const [monthCountData, setMonthCountData] = useState([]); // [{ month: 1, count: 20 }, …]
  const [reasonsData, setReasonsData] = useState([]);     // [{ reason: '…', count: 5 }, …]

  // Узнаём текущий год и месяц
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // JS возвращает 0–11, а API ждёт 1–12

  // ========== State и refs для вкладки «Сводные отчёты» ==========
  const [query, setQuery] = useState('');       // текст поиска
  const [refreshKey, setRefreshKey] = useState(0); // чтобы прокинуть в таблицу и обновить её
  const fileInputRef = useRef(null);

  // Обработчики для «Сводные отчёты»
  const handleSearch = () => {
    // При клике «поиск» в данном примере мы просто обновляем состояние query,
    // и компонент SummaryReportsTable (внутри) должен за счёт пропса query подгрузить фильтрованные данные.
    // Если у вас иная логика — скорректируйте здесь.
    // Например, можно вызывать API напрямую и передавать результат в таблицу.
    console.log('Ищем:', query);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const allowed = ['.xls', '.xlsx', '.doc', '.docx', '.pdf'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) {
      alert('Можно загружать только документы: xls, xlsx, doc, docx, pdf');
      return;
    }
    uploadReport(file);
  };

  const uploadReport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_BASE_URL}/report/upload_report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // После успешной загрузки просто «инкрементируем» refreshKey,
      // чтобы дочерний компонент SummaryReportsTable заново подгрузил данные
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error('Ошибка загрузки отчёта:', err);
      alert('Не удалось загрузить файл. Смотрите консоль для деталей.');
    }
  };

  const handleDownloadMonthReport = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/breake_problem/summary/doc`,
      { responseType: 'blob' }
    );

    // Формируем имя файла: Сводный_отчёт_{MM}_{YYYY}.docx
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const filename = `Сводный_отчёт_${month}_${year}.docx`;

    // Создаём ссылку и «кликаем» по ней, чтобы браузер скачал blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Ошибка при загрузке отчёта:', error);
    alert('Не удалось скачать отчёт. Проверьте консоль для деталей.');
  }
};

  // ========== useEffect для загрузки данных «Сводного анализа» ==========
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // 1) Процент продуктивности за текущий месяц
        const summaryRes = await axios.get(
          `${API_BASE_URL}/breake_problem/summary?year=${currentYear}&month=${currentMonth}`
        );
        // Ожидаем примерно:
        // summaryRes.data = { total_requests: 10, closed_requests: 8, open_requests: 2, productivity_pct: 80, … }

        // 2) Количество поломок по месяцам за год
        const yearlyRes = await axios.get(
          `${API_BASE_URL}/breake_problem/summary/yearly?year=${currentYear}`
        );
        // Ожидаем:
        // yearlyRes.data = { year: 2025, monthly_counts: [ { month: 1, count: 21 }, … ] }

        // 3) Причины поломок за текущий месяц
        const reasonsRes = await axios.get(
          `${API_BASE_URL}/breake_problem/summary/reasons?year=${currentYear}&month=${currentMonth}`
        );
        // Ожидаем массив:
        // reasonsRes.data = [ { reason: "Трещина в трубе", count: 5 }, … ]

        // Сохраняем в state
        setProdPct(summaryRes.data.productivity_pct);
        setMonthCountData(yearlyRes.data.monthly_counts || []);
        setReasonsData(reasonsRes.data || []);
      } catch (error) {
        console.error('Ошибка при загрузке data для «Сводного анализа»:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [currentYear, currentMonth]);

  // ========== Подготовка данных для диаграмм ==========

  // 1) Doughnut “Процент продуктивности”
  const doughnutData = {
    labels: ['Продуктивность (%)', 'Остаток (%)'],
    datasets: [
      {
        data: [prodPct, 100 - prodPct],
        backgroundColor: ['#FF7900', '#3B3B40'],
        borderWidth: 0.5,             // тонкая граница между срезами
        borderColor: '#2a2c33',
        hoverOffset: 10,             // при наведении «сектор выпрыгивает» сильнее
        hoverBorderWidth: 1.5,       // при наведении рамка сегмента чуть толще
        hoverBorderColor: '#ffffff', // например, белая рамка при ховере
      },
    ],
  };

  const doughnutOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#e5e7eb', font: { size: 14 } },
      },
      title: {
        display: true,
        text: `Процент продуктивности (${currentMonth}.${currentYear})`,
        color: '#ffffff',
        font: { size: 16, weight: '500' },
      },
    },
    maintainAspectRatio: false,
  };

  // 2) Bar “Количество поломок по месяцам”
  const barLabels = Array.from({ length: 12 }, (_, i) => `${i + 1} мес.`);
  const barDataCounts = Array(12).fill(0);
  monthCountData.forEach((item) => {
    const idx = item.month - 1;
    if (idx >= 0 && idx < 12) {
      barDataCounts[idx] = item.count;
    }
  });

  const barData = {
    labels: barLabels,
    datasets: [
      {
        label: 'Количество поломок',
        data: barDataCounts,
        backgroundColor: '#FF7900',
        borderWidth: 0.5,             // тонкая рамка столбиков
        borderColor: '#2a2c33',
        hoverBackgroundColor: '#ffaa00', // более светлый оранжевый при наведении
        hoverBorderColor: '#ffffff',     // белая рамка на hover
        hoverBorderWidth: 1.5,            // толщина рамки при наведении
      },
    ],
  };

  const barOptions = {
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `Поломки по месяцам (${currentYear})`,
        color: '#ffffff',
        font: { size: 16, weight: '500' },
      },
    },
    scales: {
      x: {
        ticks: { color: '#e5e7eb', font: { size: 12 } },
        grid: { display: false },
        border: { width: 0.5, color: '#3B3B40' },
      },
      y: {
        ticks: { color: '#e5e7eb', font: { size: 12 } },
        grid: { color: '#3B3B40' },
        border: { width: 0.5, color: '#3B3B40' },
      },
    },
    maintainAspectRatio: false,
  };

  // 3) Pie “Причины поломок”
  const pieLabels = reasonsData.map((r) => r.reason);
  const pieCounts = reasonsData.map((r) => r.count);
  const pieColors = ['#FF7900', '#3B3B40', '#FFB347', '#FFA500', '#FFD580', '#FFCC99', '#E5E7EB'];

  const pieData = {
    labels: pieLabels,
    datasets: [
      {
        data: pieCounts,
        backgroundColor: pieLabels.map((_, idx) => pieColors[idx % pieColors.length]),
        borderWidth: 0.5,              // тонкая граница между срезами
        borderColor: '#2a2c33',
        hoverOffset: 10,              // более явный «выпрыгивающий» эффект
        hoverBorderWidth: 1.5,
        hoverBorderColor: '#ffffff',
      },
    ],
  };

  const pieOptions = {
    plugins: {
     legend: {
       position: 'bottom',
       align: 'center',          // чтобы элементы выравнивались по центру
       labels: { 
         color: '#e5e7eb', 
         font: { size: 11 }, 
         boxWidth: 10,          // ещё более сглаженные квадраты
         padding: 6,            // уменьшаем отступ между пунктами
         // С помощью `maxWidth` или `usePointStyle` можно сокращать подписи:
         usePointStyle: true,    // заменить квадраты на маленькие кружочки
       },
     },
      title: {
        display: true,
        text: `Причины поломок (${currentMonth}.${currentYear})`,
        color: '#ffffff',
        font: { size: 16, weight: '500' },
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <div className="summary-reports-page vvv">
      {/* ========== Верхние вкладки ========== */}
      <div className="tabs">
        <button
          className={activeTab === 'analysis' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('analysis')}
        >
          Сводный анализ
        </button>
        <button
          className={activeTab === 'reports nn' ? 'tab active nn' : 'tab nn'}
          onClick={() => setActiveTab('reports')}
        >
          Отчёты
        </button>

                <button
          className="primary-btn save lll dcd nn"
          onClick={handleDownloadMonthReport}
        >
          Скачать отчёт за месяц
        </button>
      </div>

      <div className="tab-content">
        {/* ========== Вкладка “Сводный анализ” ========== */}
        {activeTab === 'analysis' && (
          <div className="analysis-tab">
            {loading ? (
              <div className="loading-text">Загрузка данных...</div>
            ) : (
              <div className="analysis-charts">

                {/* ─────────────── Новый контейнер для трёх графиков ─────────────── */}
                <div className="charts-row">
                  {/* 1. Doughnut */}
                  <div className="chart-container">
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                  </div>

                  {/* 2. Bar */}
                  <div className="chart-container">
                    <Bar data={barData} options={barOptions} />
                  </div>

                  {/* 3. Pie */}
                  <div className="chart-container">
                    <Pie data={pieData} options={pieOptions} />
                  </div>
                </div>
                {/* ─────────────────────────────────────────────────────────────────── */}

                {/* ─────────────── Новый контейнер для таблицы ─────────────── */}
                <div className="table-row nn">
                  <div className="table-header-row">
                    <h2 style={{ color: '#e5e7eb', margin: 0, marginBottom: 10 }}>
                      Топ-10 коллекторов по приоритету ремонта
                    </h2>
                  </div>
                  <RiskPriorityTable />
                </div>
                {/* ──────────────────────────────────────────────────────────────── */}

              </div>
            )}
          </div>
        )}

        {/* ========== Вкладка “Сводные отчёты” ========== */}
        {activeTab === 'reports' && (
          <div className="reports-tab">
            <div className="check-page vvv">
              <div className="toolbar">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Поиск в таблице"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                  />
                  <button className="search-btn" onClick={handleSearch} title="Искать">
                    <FaSearch />
                  </button>
                </div>
                <button className="icon-btn" title="Фильтр">
                  <FaFilter />
                </button>
                <button className="icon-btn" title="Загрузить отчёт" onClick={handleUploadClick}>
                  <FaUpload />
                </button>
                <input
                  type="file"
                  accept=".xls,.xlsx,.doc,.docx,.pdf"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </div>

              <div className="table-area">
                <SummaryReportsTable query={query} refreshKey={refreshKey} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryReportsPage;
